import { downloadFile, requestJson } from "@/lib/api";
import type { AdminProductSummary, ClientType, Product, ProductLabelInfo, SegmentPriceInput } from "@/lib/types";

export interface AdminProductExportFilters {
  query?: string;
  category?: string;
  includeInactive?: boolean;
}

function buildExportQuery(filters: AdminProductExportFilters): string {
  const params = new URLSearchParams();
  const q = filters.query?.trim();
  if (q) params.set("q", q);
  if (filters.category && filters.category !== "ALL") params.set("category", filters.category);
  params.set("includeInactive", String(filters.includeInactive ?? true));
  return params.toString();
}

interface AdminProductSummaryDto {
  id: string;
  name: string;
  code?: number | null;
  categoryCode: string;
  imageUrl?: string | null;
  active: boolean;
  defaultUnitName: string;
  availableUnitNames: string[];
  availableCutTypeNames: string[];
  processed: boolean;
  wastePercent?: number | null;
  segments?: ClientType[] | null;
}

interface CatalogCategoryDto {
  code: string;
}

interface CatalogUnitDto {
  name: string;
}

interface CatalogCutTypeDto {
  name: string;
}

interface AdminProductDetailDto {
  id: string;
  name: string;
  category: CatalogCategoryDto;
  defaultUnit: CatalogUnitDto;
  availableUnits: CatalogUnitDto[];
  availableCutTypes: CatalogCutTypeDto[];
  imageUrl?: string | null;
  active: boolean;
  wastePercent?: number | null;
  labelInfo?: ProductLabelInfo | null;
  segments?: ClientType[] | null;
  segmentPrices?: { segment: ClientType; unitName: string; cutTypeName?: string | null; price: number }[] | null;
}

interface AdminProductUpsertDto {
  name: string;
  categoryCode: string;
  defaultUnitName: string;
  availableUnitNames: string[];
  availableCutTypeNames: string[];
  minQuantity: number;
  imageUrl?: string;
  active: boolean;
  allowCutType: boolean;
  wastePercent?: number;
  labelInfo?: ProductLabelInfo;
  segments?: ClientType[];
  segmentPrices?: { segment: ClientType; unitName: string; cutTypeName?: string | null; price: number }[];
}

interface AdminProductActiveDto {
  active: boolean;
}

function toAdminProductSummary(product: AdminProductSummaryDto): AdminProductSummary {
  return {
    id: product.id,
    name: product.name,
    code: product.code ?? undefined,
    category: product.categoryCode,
    imageUrl: product.imageUrl ?? undefined,
    defaultUnit: product.defaultUnitName,
    availableUnits: product.availableUnitNames,
    availableCuts: product.availableCutTypeNames ?? [],
    active: product.active,
    isProcessed: product.processed,
    wastePercent: product.wastePercent ?? undefined,
    segments: product.segments ?? [],
  };
}

function toAdminProductDetail(product: AdminProductDetailDto): Product {
  const availableUnits = product.availableUnits.length > 0 ? product.availableUnits.map((unit) => unit.name) : [product.defaultUnit.name];
  const categoryCode = product.category.code;

  return {
    id: product.id,
    name: product.name,
    category: categoryCode,
    imageUrl: product.imageUrl ?? undefined,
    defaultUnit: product.defaultUnit.name,
    availableUnits,
    availableCuts: product.availableCutTypes.map((cutType) => cutType.name),
    active: product.active,
    isProcessed: categoryCode === "PROCESSADOS",
    wastePercent: product.wastePercent ?? undefined,
    labelInfo: product.labelInfo ?? undefined,
    segments: product.segments ?? [],
    // Preços reais vigentes por célula, para pré-preencher a grade de preços na edição.
    segmentPrices: (product.segmentPrices ?? []).map((entry) => ({
      segment: entry.segment,
      unit: entry.unitName,
      cut: entry.cutTypeName ?? null,
      price: entry.price,
    })),
  };
}

function toUpsertDto(product: Omit<Product, "id">): AdminProductUpsertDto {
  const availableUnits = product.availableUnits && product.availableUnits.length > 0 ? product.availableUnits : [product.defaultUnit];
  const availableCutTypeNames = product.availableCuts ?? [];

  return {
    name: product.name.trim(),
    categoryCode: product.category,
    defaultUnitName: product.defaultUnit,
    availableUnitNames: Array.from(new Set(availableUnits)),
    availableCutTypeNames: Array.from(new Set(availableCutTypeNames)),
    minQuantity: 1,
    imageUrl: product.imageUrl?.trim() || undefined,
    active: product.active !== false,
    allowCutType: availableCutTypeNames.length > 0,
    wastePercent: product.wastePercent != null ? +product.wastePercent.toFixed(2) : undefined,
    labelInfo: product.labelInfo,
    // Sempre envia o array (mesmo vazio) para que desmarcar segmentos desvincule de fato. No create,
    // o backend trata vazio como "todos"; no update, reconcilia exatamente a seleção.
    segments: product.segments ?? [],
    // Preços definidos no cadastro (só enviados quando há valores). O backend delega às Tabelas Padrão.
    segmentPrices: product.segmentPrices && product.segmentPrices.length > 0
      ? product.segmentPrices.map((entry) => ({
          segment: entry.segment,
          unitName: entry.unit,
          cutTypeName: entry.cut ?? null,
          price: entry.price,
        }))
      : undefined,
  };
}

export async function createAdminProduct(product: Omit<Product, "id">): Promise<Product> {
  const created = await requestJson<AdminProductDetailDto>("/api/v1/admin/products", {
    method: "POST",
    body: toUpsertDto(product),
  });
  return toAdminProductDetail(created);
}

export async function updateAdminProduct(id: string, product: Omit<Product, "id">): Promise<void> {
  await requestJson<void>(`/api/v1/admin/products/${id}`, {
    method: "PUT",
    body: toUpsertDto(product),
  });
}

export async function setAdminProductActive(id: string, active: boolean): Promise<void> {
  await requestJson<void>(`/api/v1/admin/products/${id}/active`, {
    method: "PATCH",
    body: { active } satisfies AdminProductActiveDto,
  });
}

/**
 * Exclusão física (definitiva) de um produto. Restrita no backend a ADMIN/ADMINISTRATIVO; retorna
 * 409 (com mensagem) quando o produto possui movimentações e não pode ser removido fisicamente.
 */
export async function deleteAdminProduct(id: string): Promise<void> {
  await requestJson<void>(`/api/v1/admin/products/${id}`, { method: "DELETE" });
}

export async function fetchAdminProducts(segment?: ClientType): Promise<AdminProductSummary[]> {
  const path = segment ? `/api/v1/admin/products?segment=${segment}` : "/api/v1/admin/products";
  const products = await requestJson<AdminProductSummaryDto[]>(path);
  return products.map(toAdminProductSummary);
}

export async function fetchAdminProduct(id: string): Promise<Product> {
  const product = await requestJson<AdminProductDetailDto>(`/api/v1/admin/products/${id}`);
  return toAdminProductDetail(product);
}

/** Exporta a listagem de produtos em PDF, respeitando os filtros aplicados na tela. */
export async function exportAdminProductsPdf(filters: AdminProductExportFilters): Promise<void> {
  await downloadFile(`/api/v1/admin/products/export.pdf?${buildExportQuery(filters)}`, "cadastro_produtos.pdf");
}

/** Exporta a listagem de produtos em XLSX (Excel), respeitando os filtros aplicados na tela. */
export async function exportAdminProductsXlsx(filters: AdminProductExportFilters): Promise<void> {
  await downloadFile(`/api/v1/admin/products/export.xlsx?${buildExportQuery(filters)}`, "cadastro_produtos.xlsx");
}
